// Simple paired-comparison correlations between daily triggers and outcomes
// (Menopause Score, symptom severity, hot flash frequency).
//
// Intentionally NOT formal statistics — we split each trigger's days into a
// "high" and "low" bucket, compute the mean outcome on each side, and
// surface the difference in plain English. Easy to explain, easy to act on,
// and resilient to the small sample sizes typical in the first weeks of use.

import { fetchTriggersBetween, type DailyTriggerRow } from "./triggers";
import {
  fetchLogsBetween,
  fetchHotFlashEventsBetween,
  localDateKey,
  parseLocalDateKey,
  type DailyLogEntry,
  type SymptomEvent,
} from "./dailyLogs";
import { fetchMenoScoreHistory } from "./menoScore";
import { supabase } from "@/integrations/supabase/client";

// Wearable-derived per-day facts used as the source of truth for the
// sleep and exercise trigger keys. Caffeine/alcohol/stress remain pulled
// from daily_triggers (manual self-report only).
type WearableFact = {
  // Total nightly sleep, in hours, rounded to 0.5.
  sleepHours: number | null;
  // Daytime activity in minutes — prefers explicit active_minutes,
  // falls back to a coarse estimate from steps.
  activeMinutes: number | null;
};

function estimateActiveFromSteps(steps: number | null | undefined): number | null {
  if (steps == null || !Number.isFinite(steps) || steps <= 0) return null;
  if (steps >= 12000) return 60;
  if (steps >= 10000) return 45;
  if (steps >= 7500) return 30;
  if (steps >= 5000) return 20;
  if (steps >= 3000) return 10;
  return 0;
}

async function fetchWearableFactsBetween(
  userId: string,
  startKey: string,
  endKey: string,
): Promise<Record<string, WearableFact>> {
  const { data, error } = await supabase
    .from("wearable_metrics")
    .select("metric_date, sleep_duration_minutes, active_minutes, steps, updated_at")
    .eq("user_id", userId)
    .gte("metric_date", startKey)
    .lte("metric_date", endKey)
    .order("updated_at", { ascending: true });
  if (error) {
    console.warn("[triggerCorrelations] wearable fetch failed", error);
    return {};
  }
  const out: Record<string, WearableFact> = {};
  for (const row of (data || []) as any[]) {
    const dk = row.metric_date as string;
    const sleepMin = row.sleep_duration_minutes;
    const sleepHours = sleepMin != null && Number.isFinite(Number(sleepMin))
      ? Math.round((Number(sleepMin) / 60) * 2) / 2
      : null;
    const active = row.active_minutes != null && Number.isFinite(Number(row.active_minutes))
      ? Number(row.active_minutes)
      : estimateActiveFromSteps(row.steps != null ? Number(row.steps) : null);
    // Later rows override earlier ones (most recent provider for that day wins).
    out[dk] = { sleepHours, activeMinutes: active };
  }
  return out;
}

export type ConfidenceLevel = "low" | "building" | "confident";

export type TriggerKey = "caffeine" | "alcohol" | "exercise" | "sleep" | "stress";
export type OutcomeKey = "meno_score" | "symptom_severity" | "hot_flash_frequency";

export type CorrelationInsight = {
  trigger: TriggerKey;
  outcome: OutcomeKey;
  effectSize: number;
  effectDescription: string;
  threshold: number | null;
  daysAnalyzed: number;
  confidence: ConfidenceLevel;
  direction: "helpful" | "harmful" | "mixed" | "unclear";
};

// Sort each trigger row into "high" or "low" given the trigger key. Returns
// null when the day has no usable value for that trigger. Sleep and exercise
// pull from the wearable fact map; the manual triggers pull from the row.
function bucketForTrigger(
  trigger: TriggerKey,
  row: DailyTriggerRow | null,
  wf: WearableFact | null,
): "high" | "low" | null {
  switch (trigger) {
    case "caffeine":
      if (!row) return null;
      return row.caffeine_count >= 2 ? "high" : "low";
    case "alcohol":
      if (!row) return null;
      return row.alcohol_count >= 2 ? "high" : "low";
    case "exercise": {
      const m = wf?.activeMinutes;
      if (m == null) return null;
      return m >= 30 ? "high" : "low";
    }
    case "sleep": {
      const h = wf?.sleepHours;
      if (h == null) return null;
      return h >= 7 ? "high" : "low"; // "high" = 7h+ sleep
    }
    case "stress": {
      if (!row) return null;
      const s = row.stress_level;
      if (s == null) return null;
      return s >= 3 ? "high" : "low";
    }
  }
}

function thresholdForTrigger(trigger: TriggerKey): number | null {
  switch (trigger) {
    case "caffeine": return 2;
    case "alcohol": return 2;
    case "exercise": return 30;
    case "sleep": return 7;
    case "stress": return 3;
  }
}

function mean(xs: number[]): number {
  if (xs.length === 0) return 0;
  return xs.reduce((a, b) => a + b, 0) / xs.length;
}

function confidenceFor(days: number): ConfidenceLevel {
  if (days < 7) return "low";
  if (days < 21) return "building";
  return "confident";
}

// Effect-size thresholds where the signal is too small to call.
const UNCLEAR_THRESHOLDS: Record<OutcomeKey, number> = {
  meno_score: 1,
  symptom_severity: 0.3,
  hot_flash_frequency: 0.5,
};

function directionFor(
  outcome: OutcomeKey,
  effect: number,
): CorrelationInsight["direction"] {
  if (Math.abs(effect) < UNCLEAR_THRESHOLDS[outcome]) return "unclear";
  if (outcome === "meno_score") return effect > 0 ? "helpful" : "harmful";
  // For severity and hot flashes, higher = worse.
  return effect > 0 ? "harmful" : "helpful";
}

// Build the human-readable copy. `trigger` informs phrasing of the
// "high trigger day" clause (e.g. "high caffeine days", "low-quality sleep").
function describeEffect(
  trigger: TriggerKey,
  outcome: OutcomeKey,
  effect: number,
): string {
  const abs = Math.abs(effect);
  const phrase =
    trigger === "caffeine" ? "high caffeine days"
    : trigger === "alcohol" ? "days with 2+ drinks"
    : trigger === "exercise" ? "days with 30+ minutes of activity"
    : trigger === "sleep" ? "nights with 7+ hours of sleep"
    : "higher-stress days";

  if (outcome === "meno_score") {
    if (Math.abs(effect) < UNCLEAR_THRESHOLDS.meno_score) {
      return `No clear score difference on ${phrase} yet.`;
    }
    if (effect > 0) return `Your score is ~${Math.round(abs)} points higher on ${phrase}.`;
    return `Your score is ~${Math.round(abs)} points lower on ${phrase}.`;
  }
  if (outcome === "symptom_severity") {
    if (Math.abs(effect) < UNCLEAR_THRESHOLDS.symptom_severity) {
      return `No clear symptom-severity difference on ${phrase} yet.`;
    }
    if (effect > 0) return `Symptoms run ~${abs.toFixed(1)} severity higher on ${phrase}.`;
    return `Symptoms run ~${abs.toFixed(1)} severity lower on ${phrase}.`;
  }
  // hot_flash_frequency
  if (Math.abs(effect) < UNCLEAR_THRESHOLDS.hot_flash_frequency) {
    return `No clear hot-flash difference on ${phrase} yet.`;
  }
  if (effect > 0) return `Hot flashes run ~${abs.toFixed(1)} more events on ${phrase}.`;
  return `Hot flashes run ~${abs.toFixed(1)} fewer events on ${phrase}.`;
}

export async function computeTriggerCorrelations(
  userId: string,
  lookbackDays: number = 30,
): Promise<CorrelationInsight[]> {
  const todayKey = localDateKey();
  const today = parseLocalDateKey(todayKey);
  if (!today) return [];
  const startKey = (() => {
    const d = new Date(today); d.setDate(today.getDate() - (lookbackDays - 1));
    return localDateKey(d);
  })();

  let triggers: DailyTriggerRow[] = [];
  let logs: DailyLogEntry[] = [];
  let events: SymptomEvent[] = [];
  let scoreSeries: { dateKey: string; score: number | null }[] = [];
  let wearableByDate: Record<string, WearableFact> = {};
  try {
    [triggers, logs, events, scoreSeries, wearableByDate] = await Promise.all([
      fetchTriggersBetween(userId, startKey, todayKey),
      fetchLogsBetween(userId, startKey, todayKey),
      fetchHotFlashEventsBetween(userId, startKey, todayKey),
      fetchMenoScoreHistory(userId, lookbackDays),
      fetchWearableFactsBetween(userId, startKey, todayKey),
    ]);
  } catch (e) {
    console.warn("[triggerCorrelations] fetch failed", e);
    return [];
  }

  // Index outcomes by date key.
  const scoreByDate: Record<string, number> = {};
  for (const s of scoreSeries) {
    if (s.score != null) scoreByDate[s.dateKey] = s.score;
  }
  const severityByDate: Record<string, number[]> = {};
  for (const r of logs) {
    if (r.severity == null || r.severity <= 0) continue;
    (severityByDate[r.log_date] ||= []).push(r.severity);
  }
  const meanSeverityByDate: Record<string, number> = {};
  for (const dk of Object.keys(severityByDate)) {
    meanSeverityByDate[dk] = mean(severityByDate[dk]);
  }
  const hfCountByDate: Record<string, number> = {};
  for (const ev of events) {
    if (ev.symptom_name !== "Hot flashes") continue;
    const dk = localDateKey(new Date(ev.occurred_at));
    hfCountByDate[dk] = (hfCountByDate[dk] || 0) + 1;
  }

  // Index manual triggers by date for quick lookup; build the union of all
  // dates that have ANY data (manual trigger or wearable fact). Hot flash
  // counts are treated as 0 on any day with data.
  const triggerByDate: Record<string, DailyTriggerRow> = {};
  for (const t of triggers) triggerByDate[t.log_date] = t;
  const allDates = new Set<string>([
    ...Object.keys(triggerByDate),
    ...Object.keys(wearableByDate),
  ]);

  const triggerKeys: TriggerKey[] = ["caffeine", "alcohol", "exercise", "sleep", "stress"];
  const outcomeKeys: OutcomeKey[] = ["meno_score", "symptom_severity", "hot_flash_frequency"];

  const insights: CorrelationInsight[] = [];

  for (const trigger of triggerKeys) {
    // Group dates into high/low buckets for this trigger.
    const highDates: string[] = [];
    const lowDates: string[] = [];
    for (const dk of allDates) {
      const b = bucketForTrigger(trigger, triggerByDate[dk] ?? null, wearableByDate[dk] ?? null);
      if (b === "high") highDates.push(dk);
      else if (b === "low") lowDates.push(dk);
    }

    for (const outcome of outcomeKeys) {
      const valueFor = (dk: string): number | null => {
        if (outcome === "meno_score") {
          return dk in scoreByDate ? scoreByDate[dk] : null;
        }
        if (outcome === "symptom_severity") {
          return dk in meanSeverityByDate ? meanSeverityByDate[dk] : null;
        }
        // hot_flash_frequency: treat missing as 0 when the day has any data.
        return hfCountByDate[dk] || 0;
      };

      const highVals = highDates.map(valueFor).filter((v): v is number => v != null);
      const lowVals = lowDates.map(valueFor).filter((v): v is number => v != null);
      if (highVals.length < 2 || lowVals.length < 2) continue;

      const effect = mean(highVals) - mean(lowVals);
      const daysAnalyzed = highVals.length + lowVals.length;
      const confidence = confidenceFor(daysAnalyzed);
      const direction = directionFor(outcome, effect);
      const effectDescription = describeEffect(trigger, outcome, effect);
      insights.push({
        trigger,
        outcome,
        effectSize: Number(effect.toFixed(2)),
        effectDescription,
        threshold: thresholdForTrigger(trigger),
        daysAnalyzed,
        confidence,
        direction,
      });
    }
  }

  insights.sort((a, b) => Math.abs(b.effectSize) - Math.abs(a.effectSize));
  return insights;
}

// Quick check for whether the user has any usable data (manual triggers OR
// wearable metrics) in the lookback window. Used by the UI to choose between
// empty-state copy ("start logging") and "not enough yet" copy.
export async function countTriggerDays(
  userId: string,
  lookbackDays: number = 30,
): Promise<number> {
  const todayKey = localDateKey();
  const today = parseLocalDateKey(todayKey);
  if (!today) return 0;
  const startKey = (() => {
    const d = new Date(today); d.setDate(today.getDate() - (lookbackDays - 1));
    return localDateKey(d);
  })();
  try {
    const [rows, wf] = await Promise.all([
      fetchTriggersBetween(userId, startKey, todayKey),
      fetchWearableFactsBetween(userId, startKey, todayKey),
    ]);
    const dates = new Set<string>();
    for (const r of rows) {
      if (
        r.caffeine_count > 0 || r.alcohol_count > 0 || r.stress_level != null
        // legacy rows may still carry sleep/exercise; count those too
        || r.exercise_minutes > 0 || r.sleep_hours != null || r.sleep_quality != null
      ) {
        dates.add(r.log_date);
      }
    }
    for (const dk of Object.keys(wf)) {
      const f = wf[dk];
      if ((f.sleepHours != null) || (f.activeMinutes != null)) dates.add(dk);
    }
    return dates.size;
  } catch {
    return 0;
  }
}

// ============================================================
// Report-only helper — builds the FULL comparison set for the
// trigger-correlation section that appears in the Peri Doctor
// Report and the Postmenopause Vitality Report.
//
// Reuses the same bucketing rules and thresholds as
// computeTriggerCorrelations (caffeine ≥2, alcohol ≥2, stress ≥3,
// sleep ≥7h, exercise ≥30min) and the same UNCLEAR_THRESHOLDS for
// flagging a "clear pattern". Does NOT mutate or replace
// computeTriggerCorrelations — the Insight tab path is untouched.
//
// Surfaces:
//  - primary outcome   = mean symptom severity (1–4, higher = worse)
//  - secondary outcome = hot-flash event count per day (higher = worse)
//
// A factor only appears when it has ≥3 days on BOTH the high and low
// sides. effectSize = highMean − lowMean (positive = worse on the
// "high trigger" side). isPattern = |effect| clears UNCLEAR_THRESHOLDS
// for that outcome.
// ============================================================

export type TriggerComparison = {
  trigger: TriggerKey;
  /** Plain-English factor label used in the rendered report. */
  label: string;
  /** Mean outcome value on the "high trigger" side. */
  highMean: number;
  /** Mean outcome value on the "low trigger" side. */
  lowMean: number;
  highDays: number;
  lowDays: number;
  /** highMean − lowMean. Positive = worse on the high-trigger side. */
  effectSize: number;
  /** True when |effectSize| clears UNCLEAR_THRESHOLDS for this outcome. */
  isPattern: boolean;
  confidence: ConfidenceLevel;
  outcome: OutcomeKey;
  /** Threshold cutoff used to bucket "high" days for this factor. */
  threshold: number;
};

export type TriggerReportData = {
  windowDays: number;
  /** True when at least one factor produced a usable comparison. */
  hasAnyComparable: boolean;
  /** Symptom-severity comparisons. Sorted by |effectSize| desc. */
  primary: TriggerComparison[];
  /** Hot-flash comparisons — only included when effect is notable. */
  hotFlashSecondary: TriggerComparison[];
  /** "hrv_proxy" when stress in this window comes mostly from the wearable. */
  stressSource: "manual" | "hrv_proxy" | null;
};

function factorLabel(trigger: TriggerKey, stressSource: "manual" | "hrv_proxy" | null): string {
  switch (trigger) {
    case "caffeine": return "Caffeine (2+ servings/day)";
    case "alcohol":  return "Alcohol (2+ drinks/day)";
    case "exercise": return "Exercise (30+ active minutes/day)";
    case "sleep":    return "Sleep (7+ hours/night)";
    case "stress":
      return stressSource === "hrv_proxy"
        ? "Stress (from your wearable)"
        : "Stress (self-reported)";
  }
}

export async function buildTriggerReportData(
  userId: string,
  lookbackDays: number = 30,
): Promise<TriggerReportData> {
  const empty: TriggerReportData = {
    windowDays: lookbackDays,
    hasAnyComparable: false,
    primary: [],
    hotFlashSecondary: [],
    stressSource: null,
  };
  if (!userId) return empty;

  const todayKey = localDateKey();
  const today = parseLocalDateKey(todayKey);
  if (!today) return empty;
  const startKey = (() => {
    const d = new Date(today); d.setDate(today.getDate() - (lookbackDays - 1));
    return localDateKey(d);
  })();

  let triggers: DailyTriggerRow[] = [];
  let logs: DailyLogEntry[] = [];
  let events: SymptomEvent[] = [];
  let wearableByDate: Record<string, WearableFact> = {};
  try {
    [triggers, logs, events, wearableByDate] = await Promise.all([
      fetchTriggersBetween(userId, startKey, todayKey),
      fetchLogsBetween(userId, startKey, todayKey),
      fetchHotFlashEventsBetween(userId, startKey, todayKey),
      fetchWearableFactsBetween(userId, startKey, todayKey),
    ]);
  } catch (e) {
    console.warn("[buildTriggerReportData] fetch failed", e);
    return empty;
  }

  // ---- Determine stress source over the window ----
  let manualStress = 0;
  let proxyStress = 0;
  for (const t of triggers) {
    if (t.stress_level == null) continue;
    if (t.stress_source === "hrv_proxy") proxyStress++;
    else manualStress++;
  }
  const stressSource: "manual" | "hrv_proxy" | null =
    proxyStress + manualStress === 0
      ? null
      : proxyStress >= manualStress ? "hrv_proxy" : "manual";

  // ---- Index outcomes by date key ----
  const severityByDate: Record<string, number[]> = {};
  for (const r of logs) {
    if (r.severity == null || r.severity <= 0) continue;
    (severityByDate[r.log_date] ||= []).push(r.severity);
  }
  const meanSeverityByDate: Record<string, number> = {};
  for (const dk of Object.keys(severityByDate)) {
    meanSeverityByDate[dk] = mean(severityByDate[dk]);
  }
  const hfCountByDate: Record<string, number> = {};
  for (const ev of events) {
    if (ev.symptom_name !== "Hot flashes") continue;
    const dk = localDateKey(new Date(ev.occurred_at));
    hfCountByDate[dk] = (hfCountByDate[dk] || 0) + 1;
  }

  const triggerByDate: Record<string, DailyTriggerRow> = {};
  for (const t of triggers) triggerByDate[t.log_date] = t;
  const allDates = new Set<string>([
    ...Object.keys(triggerByDate),
    ...Object.keys(wearableByDate),
  ]);

  const triggerKeys: TriggerKey[] = ["caffeine", "alcohol", "exercise", "sleep", "stress"];

  // Severity is "tracked-symptom-day" data — present only when ≥1 symptom logged.
  const severityValue = (dk: string): number | null =>
    dk in meanSeverityByDate ? meanSeverityByDate[dk] : null;
  // For hot flashes, missing = 0 on days that otherwise have data (matches
  // computeTriggerCorrelations); the bucketing already gates "data" via
  // wearable/manual presence.
  const hotFlashValue = (dk: string): number => hfCountByDate[dk] || 0;

  const MIN_DAYS_PER_SIDE = 3;
  const primary: TriggerComparison[] = [];
  const hotFlashSecondary: TriggerComparison[] = [];

  for (const trigger of triggerKeys) {
    const highDates: string[] = [];
    const lowDates: string[] = [];
    for (const dk of allDates) {
      const b = bucketForTrigger(trigger, triggerByDate[dk] ?? null, wearableByDate[dk] ?? null);
      if (b === "high") highDates.push(dk);
      else if (b === "low") lowDates.push(dk);
    }

    // ---- Symptom severity comparison ----
    const hiSev = highDates.map(severityValue).filter((v): v is number => v != null);
    const loSev = lowDates.map(severityValue).filter((v): v is number => v != null);
    if (hiSev.length >= MIN_DAYS_PER_SIDE && loSev.length >= MIN_DAYS_PER_SIDE) {
      const hiMean = mean(hiSev);
      const loMean = mean(loSev);
      const eff = hiMean - loMean;
      const isPattern = Math.abs(eff) >= UNCLEAR_THRESHOLDS.symptom_severity;
      const total = hiSev.length + loSev.length;
      primary.push({
        trigger,
        label: factorLabel(trigger, stressSource),
        highMean: Number(hiMean.toFixed(2)),
        lowMean: Number(loMean.toFixed(2)),
        highDays: hiSev.length,
        lowDays: loSev.length,
        effectSize: Number(eff.toFixed(2)),
        isPattern,
        confidence: confidenceFor(total),
        outcome: "symptom_severity",
        threshold: thresholdForTrigger(trigger) ?? 0,
      });
    }

    // ---- Hot-flash secondary comparison (only when notable) ----
    // Use the same "day has any data" union as the bucketing above so
    // hot-flash 0s on data-less days don't inflate either side.
    if (highDates.length >= MIN_DAYS_PER_SIDE && lowDates.length >= MIN_DAYS_PER_SIDE) {
      const hiHf = highDates.map(hotFlashValue);
      const loHf = lowDates.map(hotFlashValue);
      const hiMean = mean(hiHf);
      const loMean = mean(loHf);
      const eff = hiMean - loMean;
      if (Math.abs(eff) >= UNCLEAR_THRESHOLDS.hot_flash_frequency) {
        hotFlashSecondary.push({
          trigger,
          label: factorLabel(trigger, stressSource),
          highMean: Number(hiMean.toFixed(2)),
          lowMean: Number(loMean.toFixed(2)),
          highDays: hiHf.length,
          lowDays: loHf.length,
          effectSize: Number(eff.toFixed(2)),
          isPattern: true,
          confidence: confidenceFor(hiHf.length + loHf.length),
          outcome: "hot_flash_frequency",
          threshold: thresholdForTrigger(trigger) ?? 0,
        });
      }
    }
  }

  primary.sort((a, b) => Math.abs(b.effectSize) - Math.abs(a.effectSize));
  hotFlashSecondary.sort((a, b) => Math.abs(b.effectSize) - Math.abs(a.effectSize));

  return {
    windowDays: lookbackDays,
    hasAnyComparable: primary.length > 0 || hotFlashSecondary.length > 0,
    primary,
    hotFlashSecondary,
    stressSource,
  };
}


// ============================================================
// Report-only helper — builds 1–2 plain-English discussion-point
// lines for the STRONGEST clear pattern(s) in a TriggerReportData.
// Used by the Discussion Points section in both reports. Returns
// [] when no comparison has isPattern === true, so nothing is
// appended in the "no clear patterns" case.
//
// Selection: all comparisons (primary + hotFlashSecondary) with
// isPattern === true, sorted by |effectSize| desc, top 1 — or top
// 2 when there are multiple strong patterns. Does NOT recompute
// correlations; pure formatting over buildTriggerReportData output.
// ============================================================
function factorPhraseFor(trigger: TriggerKey, stressSource: "manual" | "hrv_proxy" | null): string {
  switch (trigger) {
    case "caffeine": return "higher-caffeine days";
    case "alcohol":  return "higher-alcohol days";
    case "exercise": return "days with 30+ minutes of activity";
    case "sleep":    return "nights with 7+ hours of sleep";
    case "stress":   return stressSource === "hrv_proxy"
      ? "higher-stress days (from your wearable)"
      : "higher-stress days";
  }
}

export function buildTriggerDiscussionPoints(data: TriggerReportData): string[] {
  if (!data || !data.hasAnyComparable) return [];
  const strong: TriggerComparison[] = [...data.primary, ...data.hotFlashSecondary]
    .filter(c => c.isPattern);
  if (strong.length === 0) return [];
  strong.sort((a, b) => Math.abs(b.effectSize) - Math.abs(a.effectSize));
  const take = strong.length >= 2 ? 2 : 1;
  const out: string[] = [];
  for (const c of strong.slice(0, take)) {
    const phrase = factorPhraseFor(c.trigger, data.stressSource);
    if (c.outcome === "symptom_severity") {
      out.push(c.effectSize > 0
        ? `Symptoms appear more severe on ${phrase} — worth discussing.`
        : `Symptoms appear less severe on ${phrase} — worth discussing.`);
    } else {
      out.push(c.effectSize > 0
        ? `Hot flashes appear more frequent on ${phrase} — worth discussing.`
        : `Hot flashes appear less frequent on ${phrase} — worth discussing.`);
    }
  }
  return out;
}
